/**
 * Pure builder: `Discovery` → `TransitoryPlaceholder` (Multi-Add Phase 2,
 * spec §2/§3).
 *
 * Extracted from `runBulkCommit` (`wiring.ts`) so the host/port derivation
 * — the only non-trivial part of recording a placeholder — is a pure,
 * framework-free function with its own unit tests. `runBulkCommit` keeps
 * the side-effecting glue (slice write + success accounting + analytics);
 * this module owns the data shaping.
 *
 * Per `think-in-systems` (ai.knowledge/global): the slice (`transitory.ts`)
 * is the primitive; this is a thin pure adapter from the discovery shape.
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
import type { Discovery } from '#~/device/discovery/index.js';
import type { Room } from '@studio/models/room';
import {
  buildPlaceholderVideo,
  toTransitoryDeviceId,
  type TransitoryKind,
  type TransitoryPlaceholder
} from '#~/device/transitory.js';

/**
 * Derive the camera host + auth port for a placeholder from a discovery.
 *
 * - **iris**: the bulk-add selector packs Iris-enabled rows with a
 *   COMPOSITE `address = "${host}:${port}"` (the row id) and the mDNS link
 *   port nested at `video.port` (~9321) — same shape `link-iris.ts`
 *   re-packs at link time (spec §5.2). Strip the composite to a clean host.
 * - **onvif**: the ONVIF service host comes from `discovery.address`
 *   (falling back to the control aspect's `host`), and the ONVIF service
 *   port (e.g. 80) from the control aspect.
 *
 * The placeholder's render-only video default is always `rtsp://host:554`
 * (`buildPlaceholderVideo`) regardless of the auth port — that is what
 * makes `isVideoDevice(toSyntheticDevice(...))` true so the tile renders
 * and Advanced Settings opens (spec §4).
 */
const resolveHostPort = (
  discovery: Discovery,
  kind: TransitoryKind
): { host: string | undefined; port: number | undefined } => {
  if (kind === 'iris') {
    const composite = discovery.address ?? '';
    const host = composite.includes(':')
      ? composite.slice(0, composite.indexOf(':'))
      : composite || undefined;
    const port =
      'video' in discovery && discovery.video && 'port' in discovery.video
        ? (discovery.video as { port?: number }).port
        : undefined;
    return { host, port };
  }

  // onvif
  const host =
    discovery.address ??
    ('control' in discovery && discovery.control
      ? (discovery.control as { host?: string }).host
      : undefined);
  const port =
    'control' in discovery && discovery.control
      ? (discovery.control as { port?: number }).port
      : undefined;
  return { host, port };
};

/**
 * Build a `TransitoryPlaceholder` for a needs-credentials bulk-add camera.
 *
 * Returns `null` when no usable host can be resolved — a placeholder's
 * RTSP video default requires a host, and a hostless placeholder could
 * never render or authenticate. The caller falls back to its failed-slice
 * skip in that case (spec §11 — the device still surfaces a Retry path).
 *
 * `createdAt` is stamped here for diagnostics/ordering (spec §2.1); the
 * slice applies NO TTL to it. `roomId` scopes the tile to the Studio the
 * camera was added to.
 */
export const buildPlaceholderForDiscovery = (
  discovery: Discovery,
  kind: TransitoryKind,
  roomId?: Room['id']
): TransitoryPlaceholder | null => {
  const { host, port } = resolveHostPort(discovery, kind);
  if (!host) {
    return null;
  }

  return {
    id: toTransitoryDeviceId(discovery.id),
    name: discovery.name,
    transitoryKind: kind,
    discoveryId: discovery.id,
    originalDiscovery: discovery,
    address: host,
    ...(port != null ? { port } : {}),
    video: buildPlaceholderVideo(host),
    createdAt: Date.now(),
    ...(roomId ? { roomId } : {})
  };
};
